import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Check, X } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/#method" className="text-sm text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors">
              The Method
            </Link>
            <Link to="/about" className="text-sm text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors">
              About
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#6B8E7C] text-xs font-medium tracking-widest uppercase mb-8 animate-fade-up opacity-0"
             style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
            For Ambitious Introverted Founders
          </p>
          
          <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.1] mb-8 animate-fade-up opacity-0" 
              style={{ fontFamily: 'Playfair Display, serif', animationDelay: '200ms', animationFillMode: 'forwards' }}>
            Build and launch your first scalable digital product in 14 days — 
            <span className="text-[#6B8E7C]"> without burnout, daily posting, or constant visibility.</span>
          </h1>
          
          <p className="text-lg text-[#2C2C2C]/70 max-w-2xl mb-10 font-light leading-relaxed animate-fade-up opacity-0"
             style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
            A structured 14-day method that turns your expertise into a profitable 
            digital asset — designed to generate income without constant visibility.
          </p>
          
          <div className="animate-fade-up opacity-0" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
            <Link to="/checkout">
              <Button 
                className="btn-primary text-base px-8 py-4"
                data-testid="hero-cta-btn"
              >
                Begin My 14-Day Build
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <p className="text-xs text-[#2C2C2C]/50 mt-4">
              Secure checkout via PayPal. Instant access upon confirmation.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2: THE PROBLEM */}
      <section className="py-24 px-6 bg-[#F2F0EB]/40">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Problem
          </p>
          <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            You don't need more motivation.
            <br />
            <span className="text-[#6B8E7C]">You need structure.</span>
          </h2>
          
          <p className="text-lg text-[#2C2C2C]/70 mb-10 font-light">
            You're thoughtful. Strategic. Capable.
            <br />
            But you're stuck in:
          </p>

          <div className="space-y-4 mb-12">
            {[
              "Endless idea refinement",
              "Overthinking your niche",
              "Starting but never finishing",
              "Posting content without converting",
              "Feeling like visibility equals exhaustion"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-[#2C2C2C]/70">
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="border-l-2 border-[#6B8E7C]/30 pl-6">
            <p className="text-xl text-[#2C2C2C]/80 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              You don't want to be loud.
            </p>
            <p className="text-xl text-[#2C2C2C]/80 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              You want to build something sustainable.
            </p>
            <p className="text-[#6B8E7C] font-medium">
              That's what Calm Scale™ was designed for.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3: THE PHILOSOPHY */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Philosophy
          </p>
          <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Built differently. On purpose.
          </h2>
          
          <p className="text-[#2C2C2C]/70 font-light mb-16">
            Calm Scale™ was designed by a founder who builds the same way — structured, asset-first, and intentionally quiet.
          </p>

          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-lg mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                No gamification.
              </h3>
              <p className="text-sm text-[#2C2C2C]/60 font-light leading-relaxed">
                No streaks. No badges. No artificial urgency. Move at your pace.
              </p>
            </div>
            <div>
              <h3 className="text-lg mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                No chat loops.
              </h3>
              <p className="text-sm text-[#2C2C2C]/60 font-light leading-relaxed">
                Structured inputs. Strategic outputs. No endless AI wandering.
              </p>
            </div>
            <div>
              <h3 className="text-lg mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                No constant visibility.
              </h3>
              <p className="text-sm text-[#2C2C2C]/60 font-light leading-relaxed">
                Build assets that work while you rest. Introvert-friendly by design.
              </p>
            </div>
          </div>

          <p className="text-center text-xl text-[#6B8E7C] mt-16 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
            This is quiet leverage.
          </p>
        </div>
      </section>

      {/* SECTION 4: THE METHOD */}
      <section id="method" className="py-24 px-6 bg-[#F2F0EB]/40">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Method
          </p>
          <h2 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Four phases. Fourteen days.
            <br />
            One transformation.
          </h2>
          <p className="text-[#2C2C2C]/60 mb-6 font-light">
            This is not a course. It's a guided build experience.
          </p>
          
          <p className="text-[#2C2C2C]/70 mb-8 font-light">
            This is the exact structure I use to build digital products — refined into a repeatable 14-day method.
          </p>

          <div className="mb-16 p-8 bg-white/50 border border-[#E5E0D6] rounded-xl">
            <p className="text-lg text-[#2C2C2C]/80 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              This is about building an asset — not just content.
            </p>
            <div className="space-y-2 text-[#2C2C2C]/60 font-light">
              <p>An offer designed to convert.</p>
              <p>A product structured for monetization.</p>
            </div>
            <p className="text-[#6B8E7C] mt-6 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quiet leverage. Built intentionally.
            </p>
          </div>

          <div className="space-y-12">
            {[
              {
                phase: "01",
                name: "Clarity",
                days: "Days 1–3",
                description: "Define your niche, ideal client, and core offer.",
                tagline: "Foundation before visibility.",
                deliverables: [
                  "A defined niche positioning statement",
                  "A clear ideal client profile",
                  "A structured core offer outline"
                ]
              },
              {
                phase: "02",
                name: "Assets",
                days: "Days 4–7",
                description: "Create your lead magnet, core product structure, and nurture sequence.",
                tagline: "Build once. Use repeatedly.",
                deliverables: [
                  "Lead magnet concept + outline",
                  "Digital product structure mapped",
                  "Foundational nurture sequence framework"
                ]
              },
              {
                phase: "03",
                name: "Authority",
                days: "Days 8–10",
                description: "Craft your positioning, bio, and credibility assets.",
                tagline: "Let your work speak.",
                deliverables: [
                  "Refined positioning statement",
                  "Professional bio",
                  "Authority messaging framework"
                ]
              },
              {
                phase: "04",
                name: "Launch",
                days: "Days 11–14",
                description: "Set pricing, map your timeline, and write your launch assets — calmly.",
                tagline: "Execute without chaos.",
                deliverables: [
                  "Pricing clarity",
                  "Launch timeline",
                  "Core launch messaging plan"
                ]
              }
            ].map((item, index) => (
              <div key={index} className="flex gap-6">
                <div className="flex-shrink-0 w-12">
                  <span className="text-xs text-[#2C2C2C]/30 font-medium">
                    {item.phase}
                  </span>
                </div>
                <div className="flex-1 pb-12 border-b border-[#E5E0D6] last:border-0">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {item.name}
                    </h3>
                    <span className="text-xs text-[#2C2C2C]/40">{item.days}</span>
                  </div>
                  <p className="text-[#2C2C2C]/70 mb-2">
                    {item.description}
                  </p>
                  <p className="text-sm text-[#6B8E7C] italic mb-4">
                    {item.tagline}
                  </p>
                  <div className="mt-4 pt-4 border-t border-[#E5E0D6]/50">
                    <p className="text-xs text-[#2C2C2C]/50 uppercase tracking-wide mb-2">
                      You leave with:
                    </p>
                    <ul className="space-y-1">
                      {item.deliverables.map((deliverable, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#2C2C2C]/60">
                          <span className="text-[#6B8E7C] mt-1">•</span>
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 bg-white/50 border border-[#E5E0D6] rounded-xl text-center">
            <p className="text-xl text-[#2C2C2C] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              By Day 14, you don't just have an idea.
            </p>
            <p className="text-lg text-[#2C2C2C]/80 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              You have a structured digital product, pricing clarity, and a defined monetization plan — so you know exactly how it generates revenue.
            </p>
            <div className="border-t border-[#E5E0D6] pt-6 mt-6">
              <p className="text-[#6B8E7C] font-medium">
                One aligned sale can cover your investment.
              </p>
              <p className="text-[#6B8E7C]/80 text-sm mt-1">
                Everything after that is leverage.
              </p>
            </div>
            
            <Link to="/checkout" className="inline-block mt-8">
              <Button className="btn-primary text-base px-8 py-4">
                Begin My 14-Day Build
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <p className="text-xs text-[#2C2C2C]/50 mt-3">
              Secure checkout via PayPal.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5: WHO THIS IS FOR */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            Is This For You?
          </p>
          <h2 className="text-3xl md:text-4xl mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™ is for women who:
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="space-y-4">
              {[
                "Want scalable income without being online all day",
                "Prefer structure over hype",
                "Value clarity over speed",
                "Want assets, not attention",
                "Are ready to finish what they start"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#6B8E7C] flex-shrink-0 mt-0.5" />
                  <span className="text-[#2C2C2C]/70">{item}</span>
                </div>
              ))}
            </div>

            <div>
              <p className="text-sm text-[#2C2C2C]/50 font-medium uppercase tracking-wide mb-4">
                This is not for:
              </p>
              <div className="space-y-3">
                {[
                  "Hustle-driven volume marketers",
                  "People looking for overnight hacks",
                  "Endless AI experimentation"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <X className="w-4 h-4 text-[#2C2C2C]/30 flex-shrink-0 mt-0.5" />
                    <span className="text-[#2C2C2C]/40 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-xl text-[#6B8E7C] text-center italic" style={{ fontFamily: 'Playfair Display, serif' }}>
            This is for builders.
          </p>
        </div>
      </section>

      {/* SECTION 6: THE OUTCOME */}
      <section className="py-24 px-6 bg-[#F2F0EB]/40">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Outcome
          </p>
          <h2 className="text-3xl md:text-4xl mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Imagine 14 days from now:
          </h2>

          <div className="space-y-4 mb-12">
            {[
              "Your niche is clear.",
              "Your offer is defined.",
              "Your product structure is mapped.",
              "Your messaging feels aligned.",
              "Your launch plan is ready."
            ].map((item, index) => (
              <p key={index} className="text-lg text-[#2C2C2C]/80 font-light">
                {item}
              </p>
            ))}
          </div>

          <div className="border-l-2 border-[#6B8E7C]/30 pl-6">
            <p className="text-xl text-[#2C2C2C]/80 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              You're not "trying to figure it out."
            </p>
            <p className="text-xl text-[#6B8E7C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              You're executing calmly.
            </p>
          </div>

          <p className="text-center text-2xl text-[#2C2C2C] mt-12 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
            That's Calm Scale.
          </p>
        </div>
      </section>

      {/* SECTION: THE INVESTMENT */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Investment
          </p>
          
          <p className="text-xl text-[#2C2C2C]/80 mb-6 font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
            If your goal is scalable income, you don't need more ideas — you need a finished asset.
          </p>
          
          <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™ is a one-time investment to build an asset designed to generate ongoing income.
          </h2>

          <div className="my-12 p-10 bg-white border border-[#E5E0D6] rounded-xl text-center">
            <p className="text-sm text-[#2C2C2C]/50 uppercase tracking-wide mb-2">Investment</p>
            <p className="text-5xl text-[#2C2C2C] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              $197
            </p>
            <p className="text-[#6B8E7C] text-sm">
              Not for information. For execution.
            </p>
          </div>

          <div className="space-y-4 mb-12">
            <p className="text-lg text-[#2C2C2C]/80 font-light">
              Most programs teach concepts.
            </p>
            <p className="text-lg text-[#2C2C2C]/80">
              <span className="text-[#6B8E7C] font-medium">Calm Scale</span> guides you through building something tangible — in 14 structured days.
            </p>
          </div>

          <p className="text-sm text-[#D4A373] italic mb-8">
            Founding Builder pricing available for this launch cycle.
          </p>

          <div className="text-center">
            <Link to="/checkout">
              <Button 
                className="btn-primary text-base px-10 py-4"
                data-testid="investment-cta-btn"
              >
                Begin My 14-Day Build
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <p className="text-xs text-[#2C2C2C]/50 mt-4">
              Secure checkout via PayPal. Instant access upon confirmation.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 7: CTA CLOSE */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to build something that lasts?
          </h2>
          <p className="text-[#2C2C2C]/60 mb-10 font-light text-lg">
            Your blueprint is waiting.
          </p>
          <Link to="/checkout">
            <Button 
              className="btn-primary text-base px-10 py-4"
              data-testid="cta-final-btn"
            >
              Begin My 14-Day Build
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <p className="text-xs text-[#2C2C2C]/50 mt-4">
            Secure checkout via PayPal. Instant access upon confirmation.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#E5E0D6]">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™
          </span>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-xs text-[#2C2C2C]/50 hover:text-[#6B8E7C] transition-colors">
              About
            </Link>
            <span className="text-xs text-[#2C2C2C]/40">
              Strategic clarity for sustainable growth.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
